export interface QuestionWords {
  readonly en: string;
  readonly nl: string;
}

export interface DiscoveryQuestion {
  readonly place: string;
  readonly prompt: QuestionWords;
  readonly choices: readonly [QuestionWords, QuestionWords, QuestionWords];
  readonly answer: 0 | 1 | 2;
  readonly explanation: QuestionWords;
  /**
   * An existing passage anchors the answer to the same evidence as the discovery book.
   */
  readonly evidence: string;
}

export const QUESTIONS: readonly DiscoveryQuestion[] = [
  {
    place: 'landmark-waag-dam',
    prompt: {
      en: 'Why do traders bring goods to the weigh house?',
      nl: 'Waarom brengen handelaars goederen naar de Waag?',
    },
    choices: [
      { en: 'To mend boats', nl: 'Boten repareren' },
      { en: 'To weigh goods before trade', nl: 'Goederen wegen voor de handel' },
      { en: 'To care for the sick', nl: 'Zieken verzorgen' },
    ],
    answer: 1,
    explanation: {
      en: 'The work of the weigh house is known, even though its exact appearance is not.',
      nl: 'We weten wat er in de Waag gebeurde, ook al weten we niet precies hoe het gebouw eruitzag.',
    },
    evidence: 'Goods are weighed here before they are traded.',
  },
  {
    place: 'landmark-lucienerf-carpenter',
    prompt: {
      en: 'A later map shows a house and timber yard. What does that tell us about this place in 1538?',
      nl: 'Een latere kaart toont een huis en een houttuin. Wat vertelt dat ons over deze plek in 1538?',
    },
    choices: [
      {
        en: 'The house and yard must already have been there.',
        nl: 'Het huis en de houttuin moeten er al zijn geweest.',
      },
      { en: 'The plot must have been empty.', nl: 'Het erf moet leeg zijn geweest.' },
      {
        en: 'We do not know whether they were already there.',
        nl: 'We weten niet of ze er toen al waren.',
      },
    ],
    answer: 2,
    explanation: {
      en: 'The sale records who bought the plot. The later map does not prove what stood there earlier.',
      nl: 'De verkoop legt vast wie het erf kocht. De latere kaart bewijst niet wat er eerder stond.',
    },
    evidence: 'Whether they already stood here in 1538 is not certain: the sale does not say so.',
  },
  {
    place: 'landmark-sint-olofspoort',
    prompt: {
      en: 'The Sint Olofspoort was built as a city gate. What was it used for in 1538?',
      nl: 'De Sint Olofspoort is gebouwd als stadspoort. Waarvoor werd zij in 1538 gebruikt?',
    },
    choices: [
      { en: 'It had become a prison.', nl: 'Zij was een gevangenis geworden.' },
      { en: 'It was a fish market.', nl: 'Zij was een vismarkt.' },
      { en: 'It was an orphanage.', nl: 'Zij was een weeshuis.' },
    ],
    answer: 0,
    explanation: {
      en: 'A building can keep its old name while its use changes. Look for the prison windows above the passage.',
      nl: 'Een gebouw kan zijn oude naam houden en toch een andere functie krijgen. Zoek de gevangenisramen boven de doorgang.',
    },
    evidence: 'In 1538 it is a prison.',
  },
  {
    place: 'landmark-kolk-gate',
    prompt: {
      en: 'Why is there an arch below the gate at the Oudezijds Kolk?',
      nl: 'Waarom zit er een boog onder de poort bij de Oudezijds Kolk?',
    },
    choices: [
      { en: 'It is a place to dry cloth.', nl: 'Het is een plek om laken te drogen.' },
      {
        en: 'It lets water pass under the Zeedijk.',
        nl: 'Het water kan er onder de Zeedijk doorstromen.',
      },
      { en: 'It is a shooting ground.', nl: 'Het is een schietbaan.' },
    ],
    answer: 1,
    explanation: {
      en: 'The road crosses above the water. The arch lets the water continue underneath.',
      nl: 'De weg steekt het water over. Door de boog kan het water eronderdoor stromen.',
    },
    evidence: 'The water runs on through a round arch.',
  },
  {
    place: 'landmark-nieuwe-kerk',
    prompt: {
      en: 'The town already had the Oude Kerk. Why did it need the Nieuwe Kerk too?',
      nl: 'De stad had al de Oude Kerk. Waarom was de Nieuwe Kerk ook nodig?',
    },
    choices: [
      {
        en: 'The growing town needed another parish church.',
        nl: 'De groeiende stad had nog een parochiekerk nodig.',
      },
      {
        en: 'The town needed a new harbour crane.',
        nl: 'De stad had een nieuwe havenkraan nodig.',
      },
      {
        en: 'The longbow archers needed a shooting ground.',
        nl: 'De handboogschutters hadden een schietbaan nodig.',
      },
    ],
    answer: 0,
    explanation: {
      en: 'As more people lived in the town, a second parish church was approved.',
      nl: 'Omdat er meer mensen in de stad woonden, werd een tweede parochiekerk goedgekeurd.',
    },
    evidence:
      'More and more people lived in the town, so the bishop approved a second parish church in 1408.',
  },
  {
    place: 'landmark-sint-pietersgasthuis',
    prompt: {
      en: 'The gasthuis scene shows bread and linen on tables. Which part is a reconstruction estimate?',
      nl: 'Bij het gasthuis zie je brood en linnen op tafels. Welk deel is een schatting in de reconstructie?',
    },
    choices: [
      {
        en: 'We know where every table stood.',
        nl: 'We weten waar elke tafel stond.',
      },
      {
        en: 'There is no evidence that the gasthuis gave care.',
        nl: 'Er zijn geen bronnen dat het gasthuis zorg gaf.',
      },
      {
        en: 'The work is supported; the table positions are estimated.',
        nl: 'Het werk is onderbouwd; de plekken van de tafels zijn geschat.',
      },
    ],
    answer: 2,
    explanation: {
      en: 'We can know what people did without knowing exactly where every object stood.',
      nl: 'We kunnen weten wat mensen deden zonder precies te weten waar elk voorwerp stond.',
    },
    evidence: 'Where each table stood is our guess.',
  },
];

export const DUTCH_QUESTIONS: Readonly<Record<string, string>> = Object.fromEntries(
  QUESTIONS.flatMap((question) =>
    [question.prompt, ...question.choices, question.explanation].map(({ en, nl }) => [en, nl])
  )
);
